import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReportStore } from '../stores/reportStore';
import { useAccountStore } from '../stores/accountStore';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useJournalStore } from '../stores/journalStore';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { DocumentTextIcon, ReceiptPercentIcon, ChartBarIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Button, CustomSelect } from '@redpanda/ui';
import FiscalYearDialog from '../components/FiscalYearDialog';

const StatCard: React.FC<{ title: string; value: number | string | React.ReactNode; color?: string; icon?: React.ReactNode }> = ({ 
  title, 
  value, 
  color = "text-gray-900 dark:text-white",
  icon 
}) => (
  <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        {icon && <div className="text-brand-600 dark:text-brand-400">{icon}</div>}
      </div>
      <div className="ml-4 w-full overflow-hidden">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{title}</h3>
        <div className={`mt-1 text-2xl font-semibold ${color} flex items-center overflow-hidden whitespace-nowrap`}>
          {value}
        </div>
      </div>
    </div>
  </div>
);


const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, error, isLoading, fetchReportData } = useReportStore();
  const accountStore = useAccountStore();
  const invoiceStore = useInvoiceStore();
  const journalStore = useJournalStore();
  const { fiscalYears, fetchFiscalYears } = useFiscalYearStore();

  const [isFiscalYearDialogOpen, setIsFiscalYearDialogOpen] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<any>(null);

  // 年度選擇狀態 - 從 localStorage 恢復，預設為當前年份
  const [selectedYear, setSelectedYear] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboardSelectedYear');
      return saved || new Date().getFullYear().toString();
    } catch {
      return new Date().getFullYear().toString();
    }
  });

  // 用於區分初始載入（讀快取）和使用者切換年份（強制重抓）
  const isInitialMount = useRef(true);

  // 檢查是否有今年的會計年度
  const currentYear = new Date().getFullYear();
  const hasCurrentYearFiscalYear = fiscalYears?.some((fy: any) =>
    fy.name.includes(currentYear.toString())
  );

  // 初始載入報表數據
  // 🚨 修正：只依賴 selectedYear，避免 fetchReportData 引用變化導致無限循環
  // 初始載入：使用快取（forceRefresh=false）；使用者切換年份：強制重抓（forceRefresh=true）
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchReportData(false, selectedYear || undefined); // 初始載入：優先讀快取
    } else {
      fetchReportData(true, selectedYear || undefined); // 年份切換：強制重新抓取
    }
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🚨 修正：移除 fetchFiscalYears 依賴，避免無限循環
  useEffect(() => {
    fetchFiscalYears(1, false); // 加載會計年度數據檢查
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 保存年度選擇到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('dashboardSelectedYear', selectedYear);
    } catch (error) {
      console.warn('無法保存年度選擇到 localStorage:', error);
    }
  }, [selectedYear]);

  // 背景預載入其他頁面資料
  // 🚨 修正：移除 store 依賴，避免無限循環
  useEffect(() => {
    const preloadAllFirstPages = () => {
      const stores = [
        { store: accountStore, name: 'accounts', fetchMethod: 'fetchAccounts' },
        { store: invoiceStore, name: 'invoices', fetchMethod: 'fetchInvoices' },
        { store: journalStore, name: 'journals', fetchMethod: 'fetchJournals' }
      ];

      // 分批載入，避免同時發送所有請求 - 完全背景執行
      stores.forEach(({ store, name, fetchMethod }, index) => {
        setTimeout(() => {
          try {
            // 列表 store 已有快取（pagination 不是 null）就不用在這裡預載
            const hasCache = ['accounts', 'invoices', 'journals'].includes(name) && (store as any).pagination !== null;

            // 🚨 修正：只在沒有快取且沒有錯誤狀態時才預載入
            const hasError = (store as any).error !== null && (store as any).error !== undefined;

            if (!hasCache && !hasError) {
              // 背景載入第一頁，不強制刷新
              (store as any)[fetchMethod](1, false);
            }
          } catch (error) {
            console.debug(`Preload ${name} failed:`, error);
          }
        }, index * 500); // 增加間隔到 500ms
      });
    };

    // 延遲 3 秒後開始預載入，確保儀表板先載入完成
    const timer = setTimeout(() => {
      preloadAllFirstPages();
    }, 3000);

    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '$0';
    const roundedValue = Math.round(value);
    return `$${roundedValue.toLocaleString()}`;
  };

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('dashboard.title')}</h1>

          {/* 年度選擇器 */}
          <div className="flex items-center space-x-4">
            {/* 載入動畫 — 只在完全沒有快取資料時顯示，有快取時背景刷新不跳動 */}
            {isLoading && !data && (
              <div className="flex items-center">
                <svg className="animate-spin h-5 w-5 text-brand-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
            <div>
              <CustomSelect
                value={selectedYear}
                onChange={setSelectedYear}
                placeholder={t('dashboard.selectYear')}
                className="h-10"
                options={[
                  { value: new Date().getFullYear().toString(), label: t('dashboard.yearLabel', { year: new Date().getFullYear() }) },
                  { value: (new Date().getFullYear() - 1).toString(), label: t('dashboard.yearLabel', { year: new Date().getFullYear() - 1 }) },
                  { value: (new Date().getFullYear() - 2).toString(), label: t('dashboard.yearLabel', { year: new Date().getFullYear() - 2 }) },
                  { value: (new Date().getFullYear() - 3).toString(), label: t('dashboard.yearLabel', { year: new Date().getFullYear() - 3 }) },
                  { value: (new Date().getFullYear() - 4).toString(), label: t('dashboard.yearLabel', { year: new Date().getFullYear() - 4 }) },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 會計年度初始化提示 - 放在第一列 */}
      {!hasCurrentYearFiscalYear && (
        <div className="bg-white dark:bg-slate-800 shadow rounded-md p-6 mb-8 border-l-4 border-brand-600">
          <div className="flex items-center">
            <div className="text-gray-400 text-3xl mr-4">📅</div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{t('dashboard.noFiscalYear')}</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
                {t('dashboard.noFiscalYearMessage', { year: currentYear })}
              </p>
              <Button
                onClick={() => {
                  // 新增模式，不預填資料
                  setSelectedFiscalYear(null);
                  setIsFiscalYearDialogOpen(true);
                }}
                className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                {t('dashboard.createFiscalYear', { year: currentYear })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title={t('dashboard.stats.totalAccounts')}
          value={data?.accountSummary?.totalAccounts ?? 0}
          icon={<ChartBarIcon className="h-8 w-8" />}
        />
        <StatCard
          title={t('dashboard.stats.totalJournals')}
          value={data?.journalSummary?.totalJournals ?? 0}
          icon={<DocumentTextIcon className="h-8 w-8" />}
        />
        <StatCard
          title={t('dashboard.stats.totalInvoices')}
          value={data?.invoiceSummary?.totalInvoices ?? 0}
          icon={<ReceiptPercentIcon className="h-8 w-8" />}
        />
        <StatCard
          title={t('dashboard.stats.totalJournalAmount')}
          value={formatCurrency(data?.journalSummary?.totalAmount)}
          color="text-green-600"
          icon={<ChartBarIcon className="h-8 w-8" />}
        />
      </div>

      {/* 發票狀態統計 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* 發票狀態 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{t('dashboard.invoiceStatus.title')}</h3>

          {/* 發票總金額 */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.invoiceStatus.totalAmount')}</span>
              <span className="text-lg font-semibold text-blue-600">
                {formatCurrency(data?.invoiceSummary?.totalAmount)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 flex overflow-hidden">
              {(data?.invoiceSummary?.totalAmount ?? 0) > 0 ? (
                <>
                  <div
                    className="bg-green-500 h-2"
                    style={{
                      width: `${Math.round((data.invoiceSummary.paidAmount / data.invoiceSummary.totalAmount) * 100)}%`
                    }}
                    title={`已付款：${formatCurrency(data.invoiceSummary.paidAmount)}`}
                  ></div>
                  <div
                    className="bg-orange-500 h-2"
                    style={{
                      width: `${Math.round((data.invoiceSummary.unpaidAmount / data.invoiceSummary.totalAmount) * 100)}%`
                    }}
                    title={`未付款：${formatCurrency(data.invoiceSummary.unpaidAmount)}`}
                  ></div>
                </>
              ) : null}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{t('dashboard.invoiceStatus.paid')} {data?.invoiceSummary?.totalAmount > 0 ? Math.round((data.invoiceSummary.paidAmount / data.invoiceSummary.totalAmount) * 100) : 0}%</span>
              <span>{t('dashboard.invoiceStatus.unpaid')} {data?.invoiceSummary?.totalAmount > 0 ? Math.round((data.invoiceSummary.unpaidAmount / data.invoiceSummary.totalAmount) * 100) : 0}%</span>
            </div>
          </div>

          {/* 已付款金額 */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.invoiceStatus.paidAmount')}</span>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(data?.invoiceSummary?.paidAmount)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{
                  width: `${data?.invoiceSummary?.totalAmount > 0
                    ? Math.round((data.invoiceSummary.paidAmount / data.invoiceSummary.totalAmount) * 100)
                    : 0}%`
                }}
              ></div>
            </div>
          </div>

          {/* 未付款金額 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.invoiceStatus.unpaidAmount')}</span>
              <span className="text-lg font-semibold text-orange-600">
                {formatCurrency(data?.invoiceSummary?.unpaidAmount)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full"
                style={{
                  width: `${data?.invoiceSummary?.totalAmount > 0
                    ? Math.round((data.invoiceSummary.unpaidAmount / data.invoiceSummary.totalAmount) * 100)
                    : 0}%`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* 應收應付帳款統計 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{t('dashboard.accountsReceivablePayable.title')}</h3>

          {/* 應收帳款 */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.accountsReceivablePayable.receivableTotal')}</span>
              <div className="text-right">
                <span className="text-lg font-semibold text-blue-600">
                  {formatCurrency(data?.accountsReceivable?.totalAmount ?? 0)}
                </span>
                <div className="text-xs text-gray-500">
                  {(() => {
                    const receivable = data?.accountsReceivable?.totalAmount ?? 0;
                    const payable = data?.accountsPayable?.totalAmount ?? 0;
                    const total = receivable + payable;
                    return total > 0 ? `${Math.round((receivable / total) * 100)}%` : '0%';
                  })()}
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${(() => {
                    const receivable = data?.accountsReceivable?.totalAmount ?? 0;
                    const payable = data?.accountsPayable?.totalAmount ?? 0;
                    const total = receivable + payable;
                    if (total === 0) return 0;
                    return Math.max(5, (receivable / total) * 100);
                  })()}%`
                }}
              ></div>
            </div>
          </div>

          {/* 應付帳款 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.accountsReceivablePayable.payableTotal')}</span>
              <div className="text-right">
                <span className="text-lg font-semibold text-orange-600">
                  {formatCurrency(data?.accountsPayable?.totalAmount ?? 0)}
                </span>
                <div className="text-xs text-gray-500">
                  {(() => {
                    const receivable = data?.accountsReceivable?.totalAmount ?? 0;
                    const payable = data?.accountsPayable?.totalAmount ?? 0;
                    const total = receivable + payable;
                    return total > 0 ? `${Math.round((payable / total) * 100)}%` : '0%';
                  })()}
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full"
                style={{
                  width: `${(() => {
                    const receivable = data?.accountsReceivable?.totalAmount ?? 0;
                    const payable = data?.accountsPayable?.totalAmount ?? 0;
                    const total = receivable + payable;
                    if (total === 0) return 0;
                    return Math.max(5, (payable / total) * 100);
                  })()}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* 新增第三排 - 本月收支對比和待處理事項 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* 本月收支對比 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{t('dashboard.monthlyComparison.title')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.monthlyComparison.income')}</span>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(data?.monthlyIncome ?? 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.monthlyComparison.expense')}</span>
              <span className="text-lg font-semibold text-red-600">
                {formatCurrency(data?.monthlyExpense ?? 0)}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{t('dashboard.monthlyComparison.netIncome')}</span>
                <span className={`text-lg font-semibold ${(data?.monthlyIncome ?? 0) - (data?.monthlyExpense ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency((data?.monthlyIncome ?? 0) - (data?.monthlyExpense ?? 0))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 待處理事項計數 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{t('dashboard.pendingTasks.title')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="text-2xl font-bold text-yellow-600">{data?.pendingJournals ?? 0}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{t('dashboard.pendingTasks.pendingJournals')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-blue-600">{data?.pendingReconciliation ?? 0}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{t('dashboard.pendingTasks.pendingReconciliation')}</div>
              <button
                className="text-xs text-brand-600 hover:text-brand-700 underline mt-1"
                onClick={() => navigate('/bank-reconciliation')}
              >
                {t('dashboard.pendingTasks.goToReconciliation')}
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* 會計年度創建對話框 */}
      <FiscalYearDialog
        isOpen={isFiscalYearDialogOpen}
        onClose={() => {
          setIsFiscalYearDialogOpen(false);
          setSelectedFiscalYear(null);
        }}
        onFiscalYearCreated={() => {
          setIsFiscalYearDialogOpen(false);
          setSelectedFiscalYear(null);
          fetchFiscalYears(1, true); // 重新加載會計年度數據
        }}
        fiscalYear={selectedFiscalYear}
      />
    </div>
  );
};

export default DashboardPage;